import { useCallback, useEffect, useState } from "react";
import { Sprout, Droplets, AlertTriangle, RefreshCw, Leaf, Dna, TrendingUp, Home } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

/* GrowTrack — the SpecStore grow-ops app. Remote telemetry for your home grow:
   every plant's stage, health and thirst, research XP, live street-market pulse.
   Reads the same /grow/state the tent computer uses — alerts tell you to get home
   before the crop suffers. */

const STAGE_META = {
  seed: { label: "Seed", color: "#a1a1aa" }, sprout: { label: "Sprout", color: "#86efac" },
  veg: { label: "Veg", color: "#4ade80" }, flower: { label: "Flower", color: "#a78bfa" },
  harvest: { label: "Ready", color: "#fbbf24" },
};

const DEMO = {
  lab: { research_xp: 160, production_skill: 3, rep: 3.8 },
  market: { supply: 20, price_mult: 1.33, mood: "steady" },
  plants: [
    { id: "p1", strain_stats: { name: "Old Harbour", potency: 6, smell_m: 18 }, stage: "flower", health: 92, care: { thirsty: false, hours_since_water: 1.2, feeds_24h: 1, overfeed_risk: false } },
    { id: "p2", strain_stats: { name: "Violet Static", potency: 4, smell_m: 14 }, stage: "veg", health: 64, care: { thirsty: true, hours_since_water: 7.5, feeds_24h: 0, overfeed_risk: false } },
    { id: "p3", strain_stats: { name: "Ember Drift", potency: 5.2, smell_m: 16 }, stage: "sprout", health: 100, care: { thirsty: false, hours_since_water: 0.4, feeds_24h: 0, overfeed_risk: false } },
  ],
  hybrids: [{ id: "h1", name: "Ember Drift" }],
};

export default function GrowTrackApp({ demo, serverId, token, accent }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [synced, setSynced] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (quiet) => {
    if (demo) { setData(DEMO); setSynced(new Date()); return; }
    if (!quiet) setBusy(true);
    try {
      const r = await api.get(`/nc/servers/${serverId}/grow/state`, { params: { token } });
      setData(r.data); setErr(""); setSynced(new Date());
    } catch (e) {
      const d = formatApiErrorDetail(e.response?.data?.detail) || "No telemetry";
      setErr(d.includes("grow") ? "no-grow" : d);
    } finally { setBusy(false); }
  }, [demo, serverId, token]);

  useEffect(() => {
    load();
    const t = setInterval(() => load(true), 20000); // live-ish telemetry
    return () => clearInterval(t);
  }, [load]);

  if (err === "no-grow") {
    return (
      <div data-testid="growtrack-empty" className="flex-1 flex flex-col items-center justify-center gap-3 p-6 text-center">
        <Home className="w-8 h-8 text-white/20" />
        <p className="font-mono text-[10px] text-white/60 leading-relaxed">No grow detected at your place.<br />Start one at the lab bench and GrowTrack picks it up automatically.</p>
      </div>
    );
  }
  if (!data) return <p className="p-6 font-mono text-[9px] text-white/40 animate-pulse uppercase tracking-[0.2em]">Linking to your grow…</p>;

  const thirsty = data.plants.filter((p) => p.care?.thirsty);
  const risk = data.plants.filter((p) => p.care?.overfeed_risk);
  const mood = data.market?.mood;

  return (
    <div data-testid="growtrack-app" className="p-3 flex flex-col gap-3 min-h-full">
      <div className="flex items-center gap-2 px-1 pb-1">
        <Sprout className="w-4 h-4 text-lime-400" />
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/80">GrowTrack</p>
        <button data-testid="growtrack-refresh" onClick={() => load()} disabled={busy}
          className="ml-auto p-1.5 rounded-full border border-white/10 text-white/50 hover:text-white transition-colors">
          <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} />
        </button>
      </div>

      {/* alerts */}
      {thirsty.length > 0 && (
        <div data-testid="growtrack-alert-thirst" className="rounded-xl border border-sky-500/40 bg-sky-950/30 px-3 py-2.5 flex items-center gap-2.5">
          <Droplets className="w-4 h-4 text-sky-300 animate-pulse shrink-0" />
          <p className="font-mono text-[9px] text-sky-200 leading-snug">{thirsty.length} plant{thirsty.length > 1 ? "s" : ""} thirsty — {thirsty.map((p) => p.strain_stats?.name).join(", ")} hasn't been watered in hours. Get home.</p>
        </div>
      )}
      {risk.length > 0 && (
        <div data-testid="growtrack-alert-feed" className="rounded-xl border border-amber-500/40 bg-amber-950/30 px-3 py-2.5 flex items-center gap-2.5">
          <AlertTriangle className="w-4 h-4 text-amber-300 shrink-0" />
          <p className="font-mono text-[9px] text-amber-200 leading-snug">Overfeed risk — one more nutrient hit today will burn {risk.length} plant{risk.length > 1 ? "s" : ""}.</p>
        </div>
      )}

      {/* plants */}
      <div className="flex flex-col gap-1.5">
        {data.plants.length === 0 && <p className="font-mono text-[9px] text-white/40 text-center py-6 uppercase tracking-[0.15em]">No living plants — the tent is empty</p>}
        {data.plants.map((p) => {
          const sm = STAGE_META[p.stage] || STAGE_META.seed;
          const health = Math.round(p.health ?? 100);
          return (
            <div key={p.id} data-testid={`growtrack-plant-${p.id}`} className="rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2.5">
              <div className="flex items-center gap-2">
                <Leaf className="w-3.5 h-3.5 shrink-0" style={{ color: sm.color }} />
                <p className="font-sans font-semibold text-xs text-white truncate">{p.strain_stats?.name || p.strain}</p>
                <span className="ml-auto font-mono text-[8px] uppercase tracking-[0.1em] px-1.5 py-0.5 rounded-full border" style={{ color: sm.color, borderColor: `${sm.color}55` }}>{sm.label}</span>
              </div>
              <div className="flex items-center gap-2 mt-2">
                <span className="font-mono text-[7px] text-white/40 uppercase w-10">health</span>
                <div className="flex-1 h-1.5 bg-black/60 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${health}%`, backgroundColor: health > 70 ? "#4ade80" : health > 40 ? "#fbbf24" : "#ef4444" }} />
                </div>
                <span className="font-mono text-[8px] text-white/60 w-8 text-right">{health}%</span>
              </div>
              <div className="flex items-center gap-3 mt-1.5">
                <span className={`font-mono text-[7px] uppercase tracking-[0.1em] flex items-center gap-1 ${p.care?.thirsty ? "text-sky-300" : "text-white/35"}`}>
                  <Droplets className="w-2.5 h-2.5" /> {p.care?.thirsty ? `${p.care.hours_since_water}h dry` : "watered"}
                </span>
                <span className="font-mono text-[7px] text-white/35 uppercase tracking-[0.1em]">feeds today: {p.care?.feeds_24h ?? 0}/3</span>
                <span className="font-mono text-[7px] text-white/35 uppercase tracking-[0.1em] ml-auto">smell {p.strain_stats?.smell_m ?? "?"}m</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* lab + market strip */}
      <div className="grid grid-cols-2 gap-1.5">
        <div className="rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2.5">
          <p className="font-mono text-[7px] uppercase tracking-[0.15em] text-white/40 flex items-center gap-1"><Dna className="w-2.5 h-2.5" /> Research</p>
          <p className="font-mono font-bold text-sm text-lime-300 mt-0.5">{data.lab?.research_xp ?? 0} XP</p>
          <p className="font-mono text-[7px] text-white/40">{data.hybrids?.length || 0} hybrid{data.hybrids?.length === 1 ? "" : "s"} stabilized</p>
        </div>
        <div className="rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2.5">
          <p className="font-mono text-[7px] uppercase tracking-[0.15em] text-white/40 flex items-center gap-1"><TrendingUp className="w-2.5 h-2.5" /> Street</p>
          <p className="font-mono font-bold text-sm mt-0.5" style={{ color: mood === "tight" ? "#4ade80" : mood === "flooded" ? "#ef4444" : "#fbbf24" }}>
            {Math.round((data.market?.price_mult ?? 1) * 100)}%
          </p>
          <p className="font-mono text-[7px] text-white/40">market {mood || "steady"}</p>
        </div>
      </div>
      <p className="mt-auto font-mono text-[7px] text-white/30 text-center uppercase tracking-[0.2em]">
        {synced ? `synced ${synced.toLocaleTimeString()}` : ""} · telemetry from your lab bench
      </p>
    </div>
  );
}
